import { ref, computed } from "vue";
import { z } from "zod";
import { structuredValidate, type ValidationErrors } from "@/utils/validation";

/**
 * Composable for managing form validation state with Zod schemas.
 * Provides reactive validation errors and utilities for validating data,
 * checking field-specific errors, and clearing validation state.
 */
export function useValidation<T>(schema: z.ZodSchema<T>) {
	const validationErrors = ref<ValidationErrors>({});

	/**
	 * Validates data against the schema and updates internal error state.
	 * @param data - The data to validate
	 * @returns Object with success flag and either validated data or errors
	 */
	const validate = (data: unknown) => {
		const result = structuredValidate(schema, data);

		if (result.success) {
			validationErrors.value = {};
			return { success: true, data: result.data };
		}

		validationErrors.value = result.errors;
		return { success: false, errors: result.errors };
	};

	/**
	 * Computed property that returns true if there are any validation errors.
	 */
	const hasErrors = computed(
		() => Object.keys(validationErrors.value).length > 0,
	);

	/**
	 * Gets the error message for a specific field path.
	 * @param fieldPath - Dot-notation path to the field (e.g., "user.name")
	 * @returns Error message or undefined if no error exists
	 */
	const getFieldError = (fieldPath: string) =>
		validationErrors.value[fieldPath];

	/**
	 * Clears all validation errors from internal state.
	 */
	const clearErrors = () => {
		validationErrors.value = {};
	};

	return {
		/** Reactive object containing current validation errors keyed by field path */
		validationErrors: computed(() => validationErrors.value),
		validate,
		hasErrors,
		getFieldError,
		clearErrors,
	};
}
